// Lightweight, dependency-free matcher for the Help Assistant. Pure functions so
// behavior is deterministic and easy to reason about. Matches a free-text question
// (ES or EN) against the curated HOWTOS, scoring by token overlap and boosting tasks
// the current user can actually perform.

import { GOVERNANCE_RULES, type GovernanceRule } from "@costadorada/shared";
import { HOWTOS, type HowTo, type HowToRole } from "@/data/howto";

/** Lowercase + strip diacritics so "cómo" matches "como", "creé" matches "cree". */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Common filler words (ES + EN) that shouldn't drive matches.
const STOP = new Set([
  "como", "hago", "puedo", "quiero", "necesito", "donde", "que", "para", "un", "una", "el",
  "la", "los", "las", "de", "del", "mi", "mis", "se", "puede", "ver", "y", "o", "en",
  "how", "do", "i", "can", "to", "the", "a", "an", "my", "is", "where", "want", "need", "of",
]);

function tokens(text: string): string[] {
  return normalize(text)
    .split(" ")
    .filter((w) => w.length > 1 && !STOP.has(w));
}

/** Pre-normalized haystack per entry: title (ES+EN) + keywords + who. */
function haystack(h: HowTo): string {
  return normalize(
    [h.titleEs, h.titleEn, h.whoEs, h.whoEn, ...h.keywords].join(" ")
  );
}

export interface HowToMatch {
  howto: HowTo;
  score: number;
  /** True when the current role can perform this task. */
  allowed: boolean;
}

/**
 * Rank how-to entries for a query. Empty query returns the curated list ordered
 * with role-relevant tasks first. Results the role can't perform are still
 * returned (allowed=false) so the UI can show them greyed with a role tag.
 */
export function searchHowTo(query: string, role?: HowToRole | null): HowToMatch[] {
  const qTokens = tokens(query);
  const roleOf = (h: HowTo) => (role ? h.roles.includes(role) : true);

  if (qTokens.length === 0) {
    return HOWTOS.map((howto) => ({ howto, score: 0, allowed: roleOf(howto) })).sort(
      (a, b) => Number(b.allowed) - Number(a.allowed)
    );
  }

  const matches: HowToMatch[] = [];
  for (const howto of HOWTOS) {
    const hay = haystack(howto);
    const hayTokens = new Set(hay.split(" "));
    let score = 0;
    for (const qt of qTokens) {
      if (hayTokens.has(qt)) score += 3; // exact token hit (keyword/title word)
      else if (hay.includes(qt)) score += 1; // substring hit (e.g. "permis" in "permiso")
    }
    if (score === 0) continue;
    const allowed = roleOf(howto);
    if (allowed) score += 1; // gentle boost so actionable tasks rank first
    matches.push({ howto, score, allowed });
  }

  return matches.sort((a, b) => b.score - a.score || Number(b.allowed) - Number(a.allowed));
}

// ============ Governance rules search ============
// Same token-overlap scoring, over the cited rules catalog. Unlike how-tos there is
// no role gating — every user may read the association's rules.

/** Pre-normalized haystack per rule: title + summary + citation + keywords (ES+EN). */
function ruleHaystack(r: GovernanceRule): string {
  return normalize(
    [r.titleEs, r.titleEn, r.summaryEs, r.summaryEn, r.citation, ...r.keywords].join(" ")
  );
}

export interface RuleMatch {
  rule: GovernanceRule;
  score: number;
}

/**
 * Rank governance rules for a query. Empty query returns []
 * (the widget only shows rules once the user starts typing).
 */
export function searchRules(query: string): RuleMatch[] {
  const qTokens = tokens(query);
  if (qTokens.length === 0) return [];

  const matches: RuleMatch[] = [];
  for (const rule of GOVERNANCE_RULES) {
    const hay = ruleHaystack(rule);
    const hayTokens = new Set(hay.split(" "));
    let score = 0;
    for (const qt of qTokens) {
      if (hayTokens.has(qt)) score += 3; // exact token hit
      else if (hay.includes(qt)) score += 1; // substring hit
    }
    if (score === 0) continue;
    matches.push({ rule, score });
  }

  return matches.sort((a, b) => b.score - a.score).slice(0, 8);
}
